import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { generateContentVideoScript } from "@helpers/aiServer";

import { DEFAULT_DURATION, DURATION_OPTIONS } from "../enums/durations";
import { DEFAULT_PERSONALITY, PERSONALITY_OPTIONS } from "../enums/personalities";
import { GENERATION_STEPS } from "../enums/steps";
import { composeRequestPrompt } from "../helpers/thread";
import { copyScript, downloadScriptDocx, downloadScriptPdf, scriptFileName } from "../helpers/exportScript";

export const STATUS = {
    IDLE: "idle",
    GENERATING: "generating",
    DONE: "done",
    ERROR: "error",
};

// Kinds of rows in the thread. Everything but USER is an assistant row;
// PENDING is the answer being written, replaced in place when it lands.
export const MESSAGE = {
    USER: "user",
    PENDING: "pending",
    SCRIPT: "script",
    ERROR: "error",
    BLOCKED: "blocked",
};

const MIN_PROMPT_LENGTH = 10;

// The server's guardrail refuses off-topic or injected prompts with this code.
const OFF_TOPIC_CODE = "off_topic";

// Swaps the minutes figure the template wrote ("… de 5 minutos") for the new
// pick, so changing the duration updates the prompt without discarding what
// else the creator typed. Null when the prompt no longer carries the figure.
function swapMinutes(text, from, to) {
    const pattern = new RegExp(`\\b${from}\\b(?=\\s*(?:minutos?|minutes?|min)\\b)`, "i");
    return pattern.test(text) ? text.replace(pattern, String(to)) : null;
}

/**
 * State and handlers of the "generate video script" modal.
 *
 * The modal is a conversation kept only in memory: every message the creator
 * sends becomes a user row, every answer an assistant row (a script card, or
 * an error). A finished script opens in the preview column at once; nothing
 * is persisted.
 *
 * Imperative like the other content modals: the parent holds a ref and calls
 * `modalRef.current.open({ id, name })` with the content to write for.
 */
export default function useGenerateVideoScript({ modalRef }) {
    const { t } = useTranslation("contentModals");

    const [open, setOpen] = useState(false);
    const [content, setContent] = useState(null);
    const [duration, setDuration] = useState(DEFAULT_DURATION);
    const [personality, setPersonality] = useState(DEFAULT_PERSONALITY);
    const [prompt, setPrompt] = useState("");
    const [promptError, setPromptError] = useState(null);
    const [status, setStatus] = useState(STATUS.IDLE);
    const [messages, setMessages] = useState([]);
    const [previewId, setPreviewId] = useState(null);
    const [previewOpen, setPreviewOpen] = useState(false);
    const [elapsed, setElapsed] = useState(0);

    const abortRef = useRef(null);
    const startedAtRef = useRef(0);
    const nextIdRef = useRef(1);
    const versionRef = useRef(0);
    const lastRequestRef = useRef(null);

    const buildPrompt = useCallback(
        (name, minutes) => t("generateScript.promptTemplate", { name: name || "", minutes }),
        [t]
    );

    modalRef.current = {
        open(target) {
            abortRef.current?.abort();

            const next = { id: target?.id, name: target?.name || "" };
            setContent(next);
            setDuration(DEFAULT_DURATION);
            setPersonality(DEFAULT_PERSONALITY);
            setPrompt(buildPrompt(next.name, DEFAULT_DURATION));
            setPromptError(null);
            setStatus(STATUS.IDLE);
            setMessages([]);
            setPreviewId(null);
            setPreviewOpen(false);
            setElapsed(0);
            versionRef.current = 0;
            lastRequestRef.current = null;
            setOpen(true);
        },
    };

    // Elapsed clock while a request is in flight — drives the step list in
    // the pending row.
    useEffect(() => {
        if (status !== STATUS.GENERATING) return undefined;

        const tick = () => setElapsed((Date.now() - startedAtRef.current) / 1000);
        tick();
        const timer = setInterval(tick, 250);

        return () => clearInterval(timer);
    }, [status]);

    // Whatever is in flight dies with the modal.
    useEffect(() => () => abortRef.current?.abort(), []);

    function push(message) {
        const id = nextIdRef.current++;
        setMessages((list) => [...list, { id, ...message }]);
        return id;
    }

    // The pending row keeps its id, so the row (and its orb) persists in the
    // DOM and animates from "writing" to the answer.
    function settle(id, message) {
        setMessages((list) => list.map((item) => (item.id === id ? { id, ...message } : item)));
    }

    function handlePromptChange(value) {
        setPrompt(value);
        if (promptError) setPromptError(null);
    }

    function handleDurationChange(minutes) {
        setPrompt((current) => {
            const swapped = swapMinutes(current, duration, minutes);
            if (swapped !== null) return swapped;
            // An empty composer (mid-thread) stays empty: the duration travels
            // with the next request on its own.
            return current;
        });
        setDuration(minutes);
    }

    function describeError(e) {
        if (e?.code === "ECONNABORTED" || e?.code === "ETIMEDOUT") return t("generateScript.errors.timeout");

        const httpStatus = e?.status ?? e?.response?.status;
        if (httpStatus === 403) return t("generateScript.errors.forbidden");
        if (httpStatus === 404) return t("generateScript.errors.notFound");
        if (httpStatus === 504) return t("generateScript.errors.timeout");

        return t("generateScript.errors.generic");
    }

    async function run(request) {
        const controller = new AbortController();
        abortRef.current = controller;
        startedAtRef.current = Date.now();
        lastRequestRef.current = request;

        setElapsed(0);
        setStatus(STATUS.GENERATING);
        const pendingId = push({ kind: MESSAGE.PENDING });

        try {
            const data = await generateContentVideoScript(
                {
                    contentId: content.id,
                    prompt: request.prompt,
                    duration: request.duration,
                    personality: request.personality,
                },
                { signal: controller.signal }
            );
            if (controller.signal.aborted) return;

            versionRef.current += 1;
            settle(pendingId, { kind: MESSAGE.SCRIPT, script: data.script, context: data.context, version: versionRef.current });
            setPreviewId(pendingId);
            setPreviewOpen(true);
            setStatus(STATUS.DONE);
        } catch (e) {
            if (controller.signal.aborted) return;

            console.error(e);
            const blocked = e?.data?.code === OFF_TOPIC_CODE;
            settle(pendingId, {
                kind: blocked ? MESSAGE.BLOCKED : MESSAGE.ERROR,
                text: blocked ? t("generateScript.errors.offTopic") : describeError(e),
            });
            setStatus(STATUS.ERROR);
        }
    }

    function handleGenerate() {
        if (!content?.id || status === STATUS.GENERATING) return;

        const text = prompt.trim();
        if (text.length < MIN_PROMPT_LENGTH) {
            setPromptError(t("generateScript.errors.promptTooShort"));
            return;
        }

        const history = messages.filter((message) => message.kind === MESSAGE.USER).map((message) => message.text);
        const requestPrompt = composeRequestPrompt({ history, text, duration, t });

        push({ kind: MESSAGE.USER, text });
        setPrompt("");
        setPromptError(null);
        run({ prompt: requestPrompt, duration, personality });
    }

    // Re-sends the last request after a failure, replacing the error row.
    function handleRetry() {
        if (!lastRequestRef.current || status === STATUS.GENERATING) return;

        setMessages((list) => (list[list.length - 1]?.kind === MESSAGE.ERROR ? list.slice(0, -1) : list));
        run(lastRequestRef.current);
    }

    function handleOpenPreview(messageId) {
        setPreviewId(messageId);
        setPreviewOpen(true);
    }

    function handleClosePreview() {
        setPreviewOpen(false);
    }

    function handleClose() {
        abortRef.current?.abort();
        setOpen(false);
        if (status === STATUS.GENERATING) setStatus(STATUS.IDLE);
    }

    const previewMessage = messages.find((message) => message.id === previewId) || null;

    const labels = {
        minutes: (count) => t("generateScript.minutes", { count }),
        page: (page, total) => t("generateScript.pdf.page", { page, total }),
    };

    // "Seção · Playlist · vídeo 2 de 3 · ~5 min" for a script's context.
    function describeContext(message) {
        if (!message?.script) return "";
        const { context, script } = message;
        const parts = [];
        const sectionName = context?.section?.name;

        if (sectionName && context?.playlist?.name && context?.position) {
            parts.push(t("generateScript.result.context", {
                section: sectionName,
                playlist: context.playlist.name,
                index: context.position.index,
                total: context.position.total,
            }));
        } else if (sectionName) {
            parts.push(t("generateScript.result.contextSingle", { section: sectionName }));
        }

        parts.push(t("generateScript.result.estimated", { count: script.estimatedMinutes }));
        return parts.join(" · ");
    }

    const contextLine = describeContext(previewMessage);

    async function handleCopy() {
        const script = previewMessage?.script;
        if (!script) return;

        try {
            await copyScript(script, { minutes: labels.minutes });
            window.setSnackbar?.(t("generateScript.result.copied"), "success");
        } catch (e) {
            window.setSnackbar?.(t("generateScript.errors.copy"), "error");
        }
    }

    function exportOptions(script) {
        return {
            subtitle: contextLine,
            footer: t("generateScript.pdf.footer"),
            fileName: `roteiro-${scriptFileName(script.title)}`,
            labels,
        };
    }

    function handleDownloadPdf() {
        const script = previewMessage?.script;
        if (!script) return;

        try {
            downloadScriptPdf(script, exportOptions(script));
        } catch (e) {
            console.error(e);
            window.setSnackbar?.(t("generateScript.preview.exportError"), "error");
        }
    }

    function handleDownloadDocx() {
        const script = previewMessage?.script;
        if (!script) return;

        try {
            downloadScriptDocx(script, exportOptions(script));
        } catch (e) {
            console.error(e);
            window.setSnackbar?.(t("generateScript.preview.exportError"), "error");
        }
    }

    const generating = status === STATUS.GENERATING;

    const stepIndex = generating
        ? GENERATION_STEPS.reduce((current, step, index) => (elapsed >= step.at ? index : current), 0)
        : GENERATION_STEPS.length - 1;

    return {
        open,
        contentName: content?.name || "",
        prompt,
        promptError,
        duration,
        durations: DURATION_OPTIONS,
        personality,
        personalities: PERSONALITY_OPTIONS,
        status,
        messages,
        previewMessage,
        previewOpen,
        contextLine,
        steps: GENERATION_STEPS,
        stepIndex,
        handlePromptChange,
        handleDurationChange,
        handlePersonalityChange: setPersonality,
        handleGenerate,
        handleRetry,
        handleOpenPreview,
        handleClosePreview,
        handleClose,
        handleCopy,
        handleDownloadPdf,
        handleDownloadDocx,
    };
}
